import t from "@/lang/lang";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  DEFAULT_MONITOR_TYPE_QUERY,
  monitorTypeFromQuery,
  monitorTypeToQuery,
} from "@/routes/base-data/processes/qpm/catalog/monitor-type-query";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { BaseMonitorsTable } from "@/routes/base-data/processes/qpm/monitors/_components/base-monitors-table";
import { MonitorAdditionalsLibraryRoutePage } from "@/routes/base-data/processes/qpm/additionals/monitor-additionals-library-route-page";

export function MonitorsRoutePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { activeTypeQuery, activeMonitorType } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("type");
    const parsed = monitorTypeFromQuery(raw);

    const query = parsed
      ? monitorTypeToQuery(parsed)
      : DEFAULT_MONITOR_TYPE_QUERY;

    return {
      activeTypeQuery: query,
      activeMonitorType: parsed ?? MonitorType.IoT,
    };
  }, [location.search]);

  // Normalize the URL (shareable deep links)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = (params.get("type") ?? "").trim().toLowerCase();
    if (current !== activeTypeQuery) {
      params.set("type", activeTypeQuery);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTypeQuery, location.search]);

  const onTypeChange = (nextType: string) => {
    const params = new URLSearchParams(location.search);
    params.set("type", nextType);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold">{t("Monitore")}</h2>
        <p className="text-muted-foreground text-sm">
          {t(
            "Übersicht aller Basis-Monitore. Sie können neue Monitore anlegen oder bestehende bearbeiten.",
          )}
        </p>
      </div>

      <section className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">{t("Basis-Monitore")}</h3>
          <p className="text-muted-foreground text-sm">
            {t(
              "Wählen Sie einen Monitor-Typ aus und bearbeiten Sie die passenden Basis-Monitore.",
            )}
          </p>
        </div>
        <Tabs value={activeTypeQuery} onValueChange={onTypeChange}>
          <TabsList>
            <TabsTrigger value="checkpoint">
              {MonitorTypeUtil.toLongString(MonitorType.Checkpoint)}
            </TabsTrigger>
            <TabsTrigger value="flyex">
              {MonitorTypeUtil.toLongString(MonitorType.FlyExterminator)}
            </TabsTrigger>
            <TabsTrigger value="iot">
              {MonitorTypeUtil.toLongString(MonitorType.IoT)}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="checkpoint">
            <BaseMonitorsTable monitorTypeKey={MonitorType.Checkpoint} />
          </TabsContent>
          <TabsContent value="flyex">
            <BaseMonitorsTable monitorTypeKey={MonitorType.FlyExterminator} />
          </TabsContent>
          <TabsContent value="iot">
            <BaseMonitorsTable monitorTypeKey={MonitorType.IoT} />
          </TabsContent>
        </Tabs>
      </section>

      <MonitorAdditionalsLibraryRoutePage monitorType={activeMonitorType} />
    </div>
  );
}
